import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Zap, ArrowRight } from 'lucide-react';
import { API } from '../utils/apiBase';

const FEATURE_META = {
  cover_letter:      { label: 'Cover Letters',   link: '/cover-letter',       color: '#002fa7' },
  linkedin_optimize: { label: 'LinkedIn Optimize', link: '/linkedin-optimizer', color: '#0077b5' },
  interview_prep:    { label: 'Interview Prep',  link: '/interview-prep',     color: '#10b981' },
  ai_rewrite:        { label: 'AI Rewrites',     link: null,                  color: '#7c3aed' },
  sage_chat:         { label: 'Sage Messages',   link: '/coach',              color: '#0f1115' },
};

function MeterBar({ label, used, limit, color, link, period }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const remaining = Math.max(0, limit - used);
  const isExhausted = remaining === 0;
  const periodLabel = period === 'day' ? 'today' : 'this month';

  return (
    <div
      className="flex items-center gap-3"
      data-testid={`usage-meter-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-28 sm:w-32 text-xs font-semibold text-[#4b5563] truncate">
        {link ? (
          <Link to={link} className="hover:text-[#002fa7] transition-colors">{label}</Link>
        ) : (
          label
        )}
      </div>
      <div className="flex-1 h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: isExhausted ? '#e11d48' : color }}
        />
      </div>
      <div className={`w-16 text-right text-xs font-bold tabular-nums ${isExhausted ? 'text-[#e11d48]' : 'text-[#4b5563]'}`}>
        {used}/{limit}
      </div>
      {isExhausted && (
        <span className="text-[9px] font-bold text-[#e11d48] uppercase tracking-wider whitespace-nowrap">
          {periodLabel}
        </span>
      )}
    </div>
  );
}

export default function FeatureUsageMeter() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/feature-usage`, { withCredentials: true })
      .then(res => { if (alive) setData(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Don't render for paid users (they have unlimited everything)
  if (!data || data.tier !== 'free') return null;

  const features = data.features || {};
  const entries = Object.entries(FEATURE_META)
    .map(([key, meta]) => ({ key, ...meta, ...(features[key] || {}) }))
    .filter(f => f.limit > 0); // only show capped features

  if (entries.length === 0) return null;

  const totalUsed = entries.reduce((sum, f) => sum + f.used, 0);
  const totalLimit = entries.reduce((sum, f) => sum + f.limit, 0);
  const anyExhausted = entries.some(f => f.used >= f.limit);

  return (
    <div
      className="mb-8 dashboard-card p-5"
      data-testid="feature-usage-meter"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-[#002fa7]" />
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#6b7280]">
            Free Plan Usage
          </h3>
          <span className="text-[10px] bg-[#f4f5f7] text-[#6b7280] px-2 py-0.5 font-semibold">
            {totalUsed} of {totalLimit} total uses
          </span>
        </div>
        {anyExhausted && (
          <Link
            to="/pricing?ref=usage_meter&utm_source=dashboard&utm_campaign=usage_meter"
            className="text-xs font-bold text-[#002fa7] hover:underline inline-flex items-center gap-1"
            data-testid="usage-meter-upgrade-link"
          >
            Upgrade for unlimited <ArrowRight size={12} />
          </Link>
        )}
      </div>

      <div className="space-y-2.5">
        {entries.map(f => (
          <MeterBar
            key={f.key}
            label={f.label}
            used={f.used}
            limit={f.limit}
            color={f.color}
            link={f.link}
            period={f.period}
          />
        ))}
      </div>

      {!anyExhausted && (
        <p className="mt-3 text-[10px] text-[#9ca3af] text-center">
          Usage resets monthly (Sage resets daily). <Link to="/pricing" className="text-[#002fa7] hover:underline">See Pro benefits</Link>
        </p>
      )}
      {anyExhausted && (
        <div className="mt-4 pt-3 border-t border-[#e5e7eb]">
          <Link
            to="/pricing?ref=usage_meter_exhausted&utm_source=dashboard&utm_campaign=usage_meter"
            className="w-full bg-[#002fa7] text-white text-sm font-bold py-2.5 hover:bg-[#001f6f] transition-colors inline-flex items-center justify-center gap-2"
            data-testid="usage-meter-upgrade-btn"
          >
            Unlock unlimited — from $4.50/mo <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}
